import { backendApi } from "@/services/backend/api";
import { updateCache } from "@/services/backend/_lib/update-cache";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import {
  Contract,
  Lifetime,
  State,
} from "@/services/backend/rlt/contracts/types/contract";
import { Filter } from "@/shared/lib/filter/filter";
import { Address } from "@/services/backend/addresses/types/address";
import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";
import { DocumentState } from "@/services/backend/documents/types/state";

const contractApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltContractCreate: build.mutation<Contract, CreateRequest>({
      query: (request) => ({
        url: `/rlt/contract`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "RltContract", id: "LIST" }],
    }),
    rltContractCreateForOffer: build.mutation<Contract, CreateForOfferRequest>({
      query: (request) => ({
        url: `/rlt/contract/for-offer`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: [
        { type: "RltContract", id: "LIST" },
        "RltOffer",
        "RltContract",
        "RltPricingByArea",
      ],
    }),
    rltContractShow: build.query<Contract, ShowRequest>({
      query: (request) => ({
        url: "/rlt/contract",
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "RltContract", id: result.id }] : [],
    }),
    rltContractPatch: build.mutation<Contract, PatchRequest>({
      query: (request) => ({
        url: `/rlt/contract`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltContract", id: request.id },
        { type: "RltContract", id: "LIST" },
      ],
    }),
    rltContractResetReferenceNumber: build.mutation<
      Contract,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltContract", id: request.id },
        { type: "RltContract", id: "LIST" },
      ],
    }),
    rltContractList: build.query<Lst<Contract>, Partial<ListRequest>>({
      query: (request) => ({
        url: "/rlt/contract/list",
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((contract) => ({
                type: "RltContract" as const,
                id: contract.id,
              })),
              { type: "RltContract", id: "LIST" },
            ]
          : [{ type: "RltContract", id: "LIST" }],
    }),
    rltContractCopyFromOffer: build.mutation<Contract, CopyFromOfferRequest>({
      query: (request) => ({
        url: `/rlt/contract/copy-from-offer`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
        "RltCalculation",
        "RltTreatment",
        "RltPricingByArea",
      ],
    }),
    rltContractAddTreatmentCycle: build.mutation<
      Contract,
      AddTreatmentCycleRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/treatment-cycle`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
        "RltPricingByArea",
      ],
    }),
    rltContractRemoveTreatmentCycle: build.mutation<
      Contract,
      RemoveTreatmentCycleRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/treatment-cycle`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
        "RltPricingByArea",
      ],
    }),
    rltContractUpdateLifetime: build.mutation<Contract, UpdateLifetimeRequest>({
      query: (request) => ({
        url: `/rlt/contract/lifetime`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractRegisterScan: build.mutation<
      Contract,
      RegisterSignedContractRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/register-signed-contract`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractTerminate: build.mutation<Contract, TerminateRequest>({
      query: (request) => ({
        url: `/rlt/contract/terminate`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractUpdateCustomerAddress: build.mutation<
      Contract,
      UpdateCustomerAddressRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/customer-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractUpdateServiceAddress: build.mutation<
      Contract,
      UpdateServiceAddressRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/service-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractUpdatePaymentTerms: build.mutation<
      Contract,
      UpdatePaymentTermsRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/payment-terms`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractUpdateSpecialAgreements: build.mutation<
      Contract,
      UpdateSpecialAgreementsRequest
    >({
      query: (request) => ({
        url: `/rlt/contract/special-agreements`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractPreviewPDF: build.query<
      { objectURL: string },
      PreviewPDFRequest
    >({
      ...revokeObjectURLOptions(),
      query: (request) => ({
        url: `/rlt/contract`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: objectURLResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
    }),
    rltContractDownloadPDF: build.mutation<
      { filename: string; objectURL: string },
      PreviewPDFRequest
    >({
      query: (request) => ({
        url: `/rlt/contract`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltContract", id: request.id },
      ],
    }),
    rltContractUpdateState: build.mutation<Contract, UpdateStateRequest>({
      query: (request) => ({
        url: `/rlt/contract/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltContract", id: request.id },
        "RltContract",
      ],
    }),
  }),
});

export const {
  useRltContractCreateMutation,
  useRltContractCreateForOfferMutation,
  useRltContractShowQuery,
  useRltContractPatchMutation,
  useRltContractResetReferenceNumberMutation,
  useRltContractListQuery,
  useRltContractCopyFromOfferMutation,
  useRltContractAddTreatmentCycleMutation,
  useRltContractRemoveTreatmentCycleMutation,
  useRltContractUpdateLifetimeMutation,
  useRltContractRegisterScanMutation,
  useRltContractTerminateMutation,
  useRltContractUpdateCustomerAddressMutation,
  useRltContractUpdateServiceAddressMutation,
  useRltContractUpdatePaymentTermsMutation,
  useRltContractUpdateSpecialAgreementsMutation,
  useLazyRltContractPreviewPDFQuery,
  useRltContractDownloadPDFMutation,
  useRltContractUpdateStateMutation,
} = contractApi;

// PatchRequest updates the document. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged.
export interface PatchRequest {
  id: string;
  referenceNumber?: string;
}

export interface CreateRequest {
  id: string;
  processId?: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateForOfferRequest {
  id: string;
  processId?: string;
  offerId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  contractState?: Filter<State>;
  lifetimeStartDate?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface CopyFromOfferRequest {
  id: string;
  offerId: string;
}

export interface AddTreatmentCycleRequest {
  id: string;
  cycle: number;
}

export interface RemoveTreatmentCycleRequest {
  id: string;
  cycle: number;
}

export interface UpdateLifetimeRequest {
  id: string;
  lifetime: Lifetime;
}

export interface RegisterSignedContractRequest {
  id: string;
  file: File;
  dataFormat: string;
  scannedBy: string;
}

export interface TerminateRequest {
  id: string;
  terminationReason: string;
}

export interface UpdateCustomerAddressRequest {
  id: string;
  customerAddress: Address;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateTreatmentPriceRelativeDiscountRequest {
  id: string;
  active: boolean;
  percentage: number;
}

export interface UpdateTreatmentPriceAbsoluteDiscountRequest {
  id: string;
  active: boolean;
  amount: number;
}

export interface UpdatePricesForExtrasRequest {
  id: string;
  hourlyRate: number;
  ratePerAccessPanel: number;
}

export interface UpdatePaymentTermsRequest {
  id: string;
  terms: string;
}

export interface UpdateSpecialAgreementsRequest {
  id: string;
  agreements: string;
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export interface PreviewPDFRequest {
  id: string;
  document: ContractDocument;
}

export enum ContractDocument {
  ALL = "all",
  CONTRACT = "contract",
  TREATMENT = "treatment",
  TREATMENT_BY_AREA = "treatment-by-area",
}

export const updateRltContractCache = updateCache(
  contractApi,
  "rltContractShow",
);
